/**
 * IconAssetDrivePicker — choose an existing Drive image for a mini-app icon slot.
 *
 * A thin modal around the shared `PickerNavigator`, filtered to the formats the
 * slot accepts. Lives in `features/files` and imports the navigator directly:
 * `DrivePickerDialog` (features/workspace) would be a files → workspace edge, i.e.
 * a cycle, and it exposes no `hideItem` so it couldn't filter to images anyway.
 *
 * PickerNavigator deliberately doesn't theme itself, so the panel below carries
 * the workspace theme class (see the CLAUDE.md rule for portalled UI).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTheme, getThemeClass, FileIcon } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { PickerNavigator, getRootFolderId, type PickerRoot, type PickerSelection } from './PickerNavigator'
import type { FileListItem } from '@papan/files/client'

/** Extensions each slot can actually use — matches the file inputs' `accept`.
 *  Name-based on purpose: it's what the user sees in Drive, and Drive's stored
 *  `mimeType` is often a generic octet-stream. The server re-sniffs the bytes
 *  regardless, so this is discovery, not a security boundary. */
const PICKABLE: Record<'bg' | 'fg', RegExp> = {
  bg: /\.(png|jpe?g)$/i,
  fg: /\.(png|jpe?g|svg)$/i,
}

export interface IconAssetDrivePickerProps {
  tenantId: string
  kind: 'bg' | 'fg'
  onPick: (sel: { id: string; name: string; mimeType: string | null }) => void
  onClose: () => void
}

export function IconAssetDrivePicker({ tenantId, kind, onPick, onClose }: IconAssetDrivePickerProps) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const rootId = getRootFolderId(tenantId)

  const [sel, setSel] = useState<PickerSelection | null>(null)
  const roots = useMemo<PickerRoot[]>(() => [{ id: rootId, label: 'papan' }], [rootId])

  // Escape closes ONLY this picker. Capture + stopPropagation so the parent
  // editor's own window Escape listener doesn't also fire and close the whole
  // editor out from under the still-open sub-modal.
  // The picker's search field handles its own Escape first (clear, then leave
  // the field); a further Escape, from outside it, closes the picker.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (e.target instanceof Element && e.target.closest('[data-testid="picker-search"]')) return
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  // Folders always survive the filter (you must be able to drill through them);
  // non-image FILES are hidden so the list is only things that can be picked.
  const hideItem = useCallback(
    (item: FileListItem) => !item.isFolder && !PICKABLE[kind].test(item.name),
    [kind],
  )

  const confirm = (s: PickerSelection | null) => {
    if (!s || s.isFolder) return
    onPick({ id: s.id, name: s.name, mimeType: s.raw?.mimeType ?? null })
  }

  return createPortal(
    // z-[70]: above the icon editor dialog (z-50) that opened this.
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative themed-bg-secondary rounded-lg shadow-2xl w-[600px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[90vh] flex flex-col border themed-border`}>
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <h3 className="text-sm font-medium themed-text">
            {kind === 'bg'
              ? t('iconEditor.drivePickBg', { defaultValue: 'Choose a background image' })
              : t('iconEditor.drivePickFg', { defaultValue: 'Choose a foreground image' })}
          </h3>
          <button onClick={onClose} className="themed-text-muted hover:themed-text" aria-label={t('iconEditor.close')}>
            <X size={16} />
          </button>
        </div>

        <PickerNavigator
          roots={roots}
          initialFolderId={rootId}
          mode="file"
          selectedId={sel?.id ?? null}
          onSelect={(item) => setSel(item)}
          onConfirm={(item) => confirm(item)}
          hideItem={hideItem}
          className="flex-1"
        />

        <div className="flex items-center justify-between px-4 py-3 border-t themed-border">
          <div className="text-xs themed-text-muted truncate max-w-[200px]">
            {sel && !sel.isFolder ? (
              <span className="flex items-center gap-1">
                <FileIcon name={sel.name} size={12} />
                {sel.name}
              </span>
            ) : (
              t('iconEditor.drivePickHint', { defaultValue: 'Select an image' })
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors">
              {t('iconEditor.cancel')}
            </button>
            <button
              onClick={() => confirm(sel)}
              disabled={!sel || sel.isFolder}
              className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50"
            >
              {t('iconEditor.drivePickConfirm', { defaultValue: 'Choose' })}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
